/**
 * Which sprite is on the bench after one is deleted from the shelf.
 *
 * Its own module, with no DOM in it, because the version of this that lived
 * inline in the delete handler was wrong in two ways and neither was catchable
 * without clicking through the app:
 *
 * 1. Deleting the sprite that was open left it sitting on the bench. The shelf
 *    row vanished, the editor did not, and you could carry on editing and
 *    saving something that no longer existed anywhere.
 * 2. Deleting anything ABOVE the open one shifted every later entry down by
 *    one, so the shelf highlighted, and the next save wrote to, the sprite
 *    below the one you were actually looking at.
 *
 * Pinned by src/shelf.test.ts.
 */

export interface Landing {
	/** Which entry should be on the bench now. */
	index: number;
	/**
	 * Whether the bench has to be reloaded.
	 *
	 * Only true when the open sprite itself went. Deleting something else just
	 * renumbers the shelf, and reloading the editor there would throw away
	 * unsaved edits for no reason.
	 */
	reopen: boolean;
}

/**
 * @param removed which entry was deleted
 * @param open which entry was on the bench
 * @param left how many entries remain, after the deletion
 */
export function afterDelete(removed: number, open: number, left: number): Landing {
	if (left <= 0) return { index: 0, reopen: false };

	// Something below the open one: nothing moves.
	if (removed > open) return { index: open, reopen: false };

	// Something above it: same sprite, one place higher up the list.
	if (removed < open) return { index: open - 1, reopen: false };

	// The open one itself. Its neighbour takes its place, which is the entry
	// that has just slid into the same position, or the new last one if it was
	// at the end.
	return { index: Math.min(removed, left - 1), reopen: true };
}
